/** Matches standard CSS `hsl(h, s%, l%)` (and optional alpha). */
const CSS_HSL_PATTERN =
  /^hsla?\(\s*([\d.]+)\s*,\s*([\d.]+)%\s*,\s*([\d.]+)%(?:\s*,\s*([\d.]+))?/i;

const HEX_PATTERN = /^#?([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;

function parseHexRgba(
  hex: string,
): { r: number; g: number; b: number; a?: number } | null {
  const match = hex.trim().match(HEX_PATTERN);
  if (!match) {
    return null;
  }

  let digits = match[1];
  if (digits.length === 3) {
    digits = digits
      .split("")
      .map((c) => c + c)
      .join("");
  }

  const r = parseInt(digits.slice(0, 2), 16);
  const g = parseInt(digits.slice(2, 4), 16);
  const b = parseInt(digits.slice(4, 6), 16);

  if ([r, g, b].some((channel) => Number.isNaN(channel))) {
    return null;
  }

  if (digits.length === 8) {
    const alphaByte = parseInt(digits.slice(6, 8), 16);
    if (Number.isNaN(alphaByte)) {
      return null;
    }
    return { r, g, b, a: alphaByte / 255 };
  }

  return { r, g, b };
}

function formatCssHsl(
  h: number,
  s: number,
  l: number,
  alpha?: number,
): string {
  if (alpha !== undefined && alpha < 1) {
    const a = Math.round(alpha * 1000) / 1000;
    return `hsla(${h}, ${s}%, ${l}%, ${a})`;
  }
  return `hsl(${h}, ${s}%, ${l}%)`;
}

function rgbToHsl(
  r: number,
  g: number,
  b: number,
): { h: number; s: number; l: number } {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;

  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;

  if (max === min) {
    return { h: 0, s: 0, l: Math.round(l * 100) };
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h = 0;
  switch (max) {
    case rn:
      h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
      break;
    case gn:
      h = ((bn - rn) / d + 2) / 6;
      break;
    default:
      h = ((rn - gn) / d + 4) / 6;
      break;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

export class ColorHelper {
  /**
   * Converts a hex color to CSS HSL: `hsl(h, s%, l%)` or `hsla(h, s%, l%, a)` for 8-digit `#RRGGBBAA`.
   * Returns null when the input is not a valid hex color.
   */
  static hexToCssHsl(hex: string): string | null {
    const rgba = parseHexRgba(hex);
    if (!rgba) {
      return null;
    }

    const { h, s, l } = rgbToHsl(rgba.r, rgba.g, rgba.b);
    return formatCssHsl(h, s, l, rgba.a);
  }

  /**
   * Returns `hsl(h, s%, l%)` for hex input; passes through values already in CSS HSL form.
   */
  static toCssHsl(color: string): string {
    const trimmed = color.trim();
    if (CSS_HSL_PATTERN.test(trimmed)) {
      return trimmed;
    }
    return ColorHelper.hexToCssHsl(trimmed) ?? trimmed;
  }

  /**
   * Adjusts the lightness of a CSS `hsl(h, s%, l%)` value by `lightnessDelta` percentage points.
   */
  static adjustCssHslLightness(color: string, lightnessDelta: number): string {
    const trimmed = color.trim();
    const match = trimmed.match(CSS_HSL_PATTERN);
    if (!match) {
      return trimmed;
    }

    const h = Math.round(parseFloat(match[1]));
    const s = Math.round(parseFloat(match[2]));
    const l = Math.max(
      0,
      Math.min(100, Math.round(parseFloat(match[3]) + lightnessDelta)),
    );

    const alpha = match[4] !== undefined ? parseFloat(match[4]) : undefined;
    return formatCssHsl(h, s, l, alpha);
  }
}
